
import { useState, useEffect } from 'react';

// CSS-based animations as fallback for performance
const cssAnimations = {
  fadeInUp: 'animate-fade-in-up',
  scaleIn: 'animate-fade-in-scale', 
  slideInFromLeft: 'animate-slide-in-left'
};

const AnimatedDiv = ({ children, animation = 'fadeInUp', delay = 0, className, useFramerMotion = false }) => {
  const [MotionComponent, setMotionComponent] = useState(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
    
    // Only load framer-motion if explicitly requested and on client-side
    if (useFramerMotion && typeof window !== 'undefined') {
      import('framer-motion').then(({ motion }) => {
        setMotionComponent(motion);
      });
    }
  }, [useFramerMotion]);

  // Fallback to CSS animations for better performance
  if (!useFramerMotion || !MotionComponent) {
    const animationClass = cssAnimations[animation] || cssAnimations.fadeInUp;
    const style = delay > 0 ? { animationDelay: `${delay}s` } : {};
    
    return (
      <div 
        className={`${className} ${animationClass}`}
        style={style}
      >
        {children}
      </div>
    );
  }

  // Enhanced framer-motion animations for when explicitly requested
  const animations = {
    fadeInUp: {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.5, ease: 'easeOut' }
    },
    scaleIn: {
      initial: { opacity: 0, scale: 0.95 },
      animate: { opacity: 1, scale: 1 },
      transition: { duration: 0.4, ease: [0.25, 1, 0.5, 1] }
    },
    slideInFromLeft: {
      initial: { opacity: 0, x: -30 },
      animate: { opacity: 1, x: 0 },
      transition: { duration: 0.6, ease: 'circOut' }
    }
  };

  const selectedAnimation = animations[animation] || animations.fadeInUp;

  return (
    <MotionComponent.div
      className={className}
      initial="initial"
      animate="animate"
      variants={{
        initial: selectedAnimation.initial,
        animate: {
          ...selectedAnimation.animate,
          transition: {
            ...selectedAnimation.transition,
            delay: delay
          }
        }
      }}
    >
      {children}
    </MotionComponent.div>
  );
};

export default AnimatedDiv;
